import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { browserSupportsWebAuthn, startAuthentication } from "@simplewebauthn/browser";
import type { PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import { Fingerprint, KeyRound, Smartphone } from "lucide-react";
import { useMfaChallenge, useMfaVerify, useMfaRecovery, useMfaTotp } from "../hooks/useMfa";
import { useMfaAttempt } from "../hooks/useMfaAttempt";
import { useAuth } from "@/providers/auth-provider";
import { ApiError } from "@/lib/api/client";
import {
  CeremonyCanceledError,
  cancelCeremonies,
  isCeremonyOwnerReleased,
  preparedCeremony,
} from "@/lib/webauthn";
import { useCeremonyOwner } from "@/hooks/useCeremonyOwner";
import { resolvePostLoginPath } from "../post-login-redirect";
import type { MfaRequiredResponse } from "../api/auth.api";

interface MfaChallengeProps {
  pending: MfaRequiredResponse;
  onRestart: () => void;
  passkeyStatusRegion: HTMLElement | null;
}

type PasskeyState = "idle" | "prompting" | "verifying" | "canceled" | "failed" | "unsupported";
type Mode = "passkey" | "totp" | "recovery";

const PASSKEY_STATUS_KEY = {
  prompting: "auth.mfa.prompting",
  verifying: "auth.mfa.verifying",
  canceled: "auth.mfa.canceled",
} as const;

export function MfaChallenge({ pending, onRestart, passkeyStatusRegion }: MfaChallengeProps) {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const ceremonyOwner = useCeremonyOwner();
  const challengeMutation = useMfaChallenge();
  const verifyMutation = useMfaVerify();
  const recoveryMutation = useMfaRecovery();
  const totpMutation = useMfaTotp();
  const attempt = useMfaAttempt();

  const supportsWebAuthn = browserSupportsWebAuthn();
  const methods = pending.methods ?? ["passkey", "recovery_code"];
  const passkeyAvailable = methods.includes("passkey") && supportsWebAuthn;
  const totpAvailable = methods.includes("totp");
  const recoveryAvailable = methods.includes("recovery_code");

  const [passkeyState, setPasskeyState] = useState<PasskeyState>(
    supportsWebAuthn ? "idle" : "unsupported",
  );
  const [mode, setMode] = useState<Mode>(
    passkeyAvailable ? "passkey" : totpAvailable ? "totp" : "recovery",
  );
  const [recoveryCode, setRecoveryCode] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [expired, setExpired] = useState(false);
  const startedRef = useRef(false);
  const challengeQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const passkeyBlockRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const verifyingRef = useRef<HTMLParagraphElement>(null);
  const retryRef = useRef<HTMLButtonElement>(null);

  const applySession = useCallback(
    (accessKey: string) => {
      login(accessKey);
      navigate({ to: resolvePostLoginPath(search.redirect) });
    },
    [login, navigate, search.redirect],
  );

  useEffect(() => {
    const remaining = Math.max(pending.expires - Date.now(), 0);
    const timer = setTimeout(() => {
      attempt.abandon();
      setExpired(true);
    }, remaining);
    return () => clearTimeout(timer);
  }, [pending.expires, attempt]);

  const markExpiredOn401 = useCallback(
    (err: unknown) => {
      if (err instanceof ApiError && err.status === 401 && Date.now() >= pending.expires) {
        attempt.abandon();
        setExpired(true);
      }
    },
    [pending.expires, attempt],
  );

  const runPasskey = useCallback(async () => {
    const token = attempt.begin();
    attempt.markInFlight(token);
    setPasskeyState("prompting");
    try {
      const assertion = await preparedCeremony(
        (signal) => {
          const request = challengeQueueRef.current.then(() => {
            if (signal.aborted || isCeremonyOwnerReleased(ceremonyOwner)) {
              throw new CeremonyCanceledError("The passkey prompt was abandoned before it opened.");
            }
            return challengeMutation.mutateAsync(pending.pendingToken);
          });
          challengeQueueRef.current = request.catch(() => {});
          return request;
        },
        ({ options }) =>
          startAuthentication({
            optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON,
          }),
        { owner: ceremonyOwner },
      );
      if (!attempt.isCurrent(token)) return;
      setPasskeyState("verifying");
      const data = await verifyMutation.mutateAsync({
        pendingToken: pending.pendingToken,
        response: assertion,
      });
      if (!attempt.settle(token)) return;
      applySession(data.accessKey);
    } catch (err) {
      if (!attempt.settle(token)) return;
      if (err instanceof ApiError) {
        markExpiredOn401(err);
        setPasskeyState("failed");
        return;
      }
      const name = err instanceof Error ? err.name : "";
      const canceled = name === "NotAllowedError" || name === "AbortError";
      if (canceled) challengeMutation.reset();
      setPasskeyState(canceled ? "canceled" : "failed");
    }
  }, [
    attempt,
    applySession,
    ceremonyOwner,
    challengeMutation,
    verifyMutation,
    pending.pendingToken,
    markExpiredOn401,
  ]);

  useEffect(() => {
    if (passkeyAvailable && !startedRef.current) {
      startedRef.current = true;
      runPasskey();
    }
  }, [passkeyAvailable, runPasskey]);

  useLayoutEffect(() => {
    if (mode !== "passkey") return;
    const active = document.activeElement;
    const onBody = active === null || active === document.body;
    const inside =
      active instanceof Node &&
      passkeyBlockRef.current !== null &&
      passkeyBlockRef.current.contains(active);
    if (!onBody && !inside) return;
    if (passkeyState === "prompting") cancelRef.current?.focus();
    else if (passkeyState === "verifying") verifyingRef.current?.focus();
    else if (passkeyState === "canceled" || passkeyState === "failed") retryRef.current?.focus();
  }, [mode, passkeyState]);

  const resetErrors = () => {
    verifyMutation.reset();
    challengeMutation.reset();
    recoveryMutation.reset();
    totpMutation.reset();
  };

  const completionPending =
    passkeyState === "verifying" ||
    verifyMutation.isPending ||
    totpMutation.isPending ||
    recoveryMutation.isPending;

  const switchMode = (next: Mode) => {
    if (completionPending) return;
    attempt.abandon();
    cancelCeremonies(ceremonyOwner);
    resetErrors();
    setMode(next);
    if (next === "passkey") {
      setPasskeyState("idle");
      runPasskey();
    }
  };

  const handleRestart = () => {
    if (completionPending) return;
    attempt.abandon();
    onRestart();
  };

  const submitTotp = async () => {
    if (totpMutation.isPending) return;
    const code = totpCode.trim();
    if (!code) return;
    const token = attempt.begin();
    attempt.markInFlight(token);
    try {
      const data = await totpMutation.mutateAsync({ pendingToken: pending.pendingToken, code });
      if (!attempt.settle(token)) return;
      applySession(data.accessKey);
    } catch (err) {
      if (!attempt.settle(token)) return;
      markExpiredOn401(err);
    }
  };

  const submitRecovery = async () => {
    if (recoveryMutation.isPending) return;
    const code = recoveryCode.trim();
    if (!code) return;
    const token = attempt.begin();
    attempt.markInFlight(token);
    try {
      const data = await recoveryMutation.mutateAsync({ pendingToken: pending.pendingToken, code });
      if (!attempt.settle(token)) return;
      applySession(data.accessKey);
    } catch (err) {
      if (!attempt.settle(token)) return;
      markExpiredOn401(err);
    }
  };

  const apiError =
    mode === "passkey"
      ? (verifyMutation.error ?? challengeMutation.error)
      : mode === "totp"
        ? totpMutation.error
        : recoveryMutation.error;

  const passkeyStatusShown =
    mode === "passkey" &&
    (passkeyState === "prompting" || passkeyState === "verifying" || passkeyState === "canceled");
  const passkeyStatus = passkeyStatusShown ? t(PASSKEY_STATUS_KEY[passkeyState]) : "";
  const announcedPasskeyStatus =
    passkeyStatusRegion === null ? null : createPortal(passkeyStatus, passkeyStatusRegion);

  if (expired) {
    return (
      <div className="text-center">
        <p role="alert" className="text-sm text-danger">
          {t("auth.mfa.expired")}
        </p>
        <button
          type="button"
          onClick={onRestart}
          className="mt-4 text-sm font-medium text-indigo transition-colors hover:text-indigo-bright"
        >
          {t("auth.mfa.backToLogin")}
        </button>
      </div>
    );
  }

  const subtitle =
    mode === "passkey"
      ? t("auth.mfa.subtitle")
      : mode === "totp"
        ? t("auth.mfa.totpSubtitle")
        : t("auth.mfa.recoverySubtitle");

  const alternatives = (
    <div className="flex flex-col items-center gap-2">
      {mode !== "passkey" && passkeyAvailable && (
        <button
          type="button"
          onClick={() => switchMode("passkey")}
          aria-disabled={completionPending || undefined}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo transition-colors hover:text-indigo-bright aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
        >
          <Fingerprint size={12} />
          {t("auth.mfa.usePasskey")}
        </button>
      )}
      {mode !== "totp" && totpAvailable && (
        <button
          type="button"
          onClick={() => switchMode("totp")}
          aria-disabled={completionPending || undefined}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo transition-colors hover:text-indigo-bright aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
        >
          <Smartphone size={12} />
          {t("auth.mfa.useTotp")}
        </button>
      )}
      {mode !== "recovery" && recoveryAvailable && (
        <button
          type="button"
          onClick={() => switchMode("recovery")}
          aria-disabled={completionPending || undefined}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo transition-colors hover:text-indigo-bright aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
        >
          <KeyRound size={12} />
          {t("auth.mfa.useRecoveryCode")}
        </button>
      )}
    </div>
  );

  return (
    <div>
      {announcedPasskeyStatus}
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-bold text-heading">{t("auth.mfa.title")}</h1>
        <p className="mt-1 text-sm text-muted">{subtitle}</p>
      </div>

      {apiError && (
        <p role="alert" className="mb-4 text-center text-xs text-danger">
          {apiError.error ?? t("common.error")}
        </p>
      )}

      {mode === "passkey" && (
        <div ref={passkeyBlockRef} className="flex flex-col items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-elevated">
            <Fingerprint size={32} className="text-indigo" />
          </div>

          {(passkeyState === "prompting" || passkeyState === "verifying") && (
            <>
              <p
                ref={passkeyState === "verifying" ? verifyingRef : undefined}
                tabIndex={passkeyState === "verifying" ? -1 : undefined}
                className="text-sm text-muted outline-none"
              >
                {passkeyState === "verifying" ? t("auth.mfa.verifying") : t("auth.mfa.prompting")}
              </p>
              {passkeyState === "prompting" && (
                <button
                  ref={cancelRef}
                  type="button"
                  onClick={(event) => {
                    if (event.detail <= 1) cancelCeremonies(ceremonyOwner);
                  }}
                  className="text-xs font-medium text-muted transition-colors hover:text-heading"
                >
                  {t("common.cancel")}
                </button>
              )}
            </>
          )}
          {passkeyState === "canceled" && (
            <p className="text-sm text-muted">{t("auth.mfa.canceled")}</p>
          )}
          {passkeyState === "failed" && !apiError && (
            <p role="alert" className="text-sm text-danger">
              {t("auth.mfa.failed")}
            </p>
          )}

          {(passkeyState === "canceled" || passkeyState === "failed") && (
            <button
              ref={retryRef}
              type="button"
              onClick={(event) => {
                if (verifyMutation.isPending) return;
                if (event.detail <= 1) runPasskey();
              }}
              aria-disabled={verifyMutation.isPending || undefined}
              className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            >
              {t("auth.mfa.retry")}
            </button>
          )}

          {alternatives}
        </div>
      )}

      {mode === "totp" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submitTotp();
          }}
          className="flex flex-col gap-4"
        >
          <input
            type="text"
            inputMode="numeric"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value)}
            placeholder="123456"
            aria-label={t("auth.mfa.totpLabel")}
            autoComplete="one-time-code"
            autoFocus
            maxLength={7}
            readOnly={totpMutation.isPending}
            aria-disabled={totpMutation.isPending || undefined}
            className="h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-center font-mono text-sm tracking-widest text-heading transition-colors focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo aria-disabled:opacity-50 hover:border-border-light"
          />
          <button
            type="submit"
            disabled={!totpMutation.isPending && !totpCode.trim()}
            aria-disabled={totpMutation.isPending || undefined}
            aria-busy={totpMutation.isPending || undefined}
            className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
          >
            {totpMutation.isPending ? t("auth.mfa.verifying") : t("auth.mfa.totpSubmit")}
          </button>
          {alternatives}
        </form>
      )}

      {mode === "recovery" && (
        <>
          {!supportsWebAuthn && methods.includes("passkey") && !totpAvailable && (
            <p className="mb-4 text-center text-xs text-muted">{t("auth.mfa.unsupported")}</p>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submitRecovery();
            }}
            className="flex flex-col gap-4"
          >
            <input
              type="text"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value)}
              placeholder={t("auth.mfa.recoveryPlaceholder")}
              aria-label={t("auth.mfa.recoveryLabel")}
              autoComplete="one-time-code"
              autoFocus
              readOnly={recoveryMutation.isPending}
              aria-disabled={recoveryMutation.isPending || undefined}
              className="h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-center font-mono text-sm tracking-wider text-heading transition-colors focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo aria-disabled:opacity-50 hover:border-border-light"
            />
            <button
              type="submit"
              disabled={!recoveryMutation.isPending && !recoveryCode.trim()}
              aria-disabled={recoveryMutation.isPending || undefined}
              aria-busy={recoveryMutation.isPending || undefined}
              className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
            >
              {recoveryMutation.isPending ? t("auth.mfa.verifying") : t("auth.mfa.recoverySubmit")}
            </button>
            {alternatives}
          </form>
        </>
      )}

      <p className="mt-6 text-center">
        <button
          type="button"
          onClick={handleRestart}
          aria-disabled={completionPending || undefined}
          className="text-xs font-medium text-muted transition-colors hover:text-heading aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
        >
          {t("auth.mfa.backToLogin")}
        </button>
      </p>
    </div>
  );
}
